import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FileSpreadsheet, Snowflake, Sun } from 'lucide-react';
import { Store } from '../../Store';
import { Button } from '../../components/ui/button';
import PageHeader from '../../components/shared/PageHeader';
import { cn } from '../../lib/utils';
import TicketTable from '../../components/TicketTable';
import TicketRangeModal from '../../components/TicketRangeModal';
import ticketService from '../../services/ticket.service';
import rifaService from '../../services/rifa.service';
import LoadingBox from '../../components/LoadingBox';
import StatusLoadingOverlay from '../../components/StatusLoadingOverlay';

const EXPORT_STATUS_KEYS = [
  'reservation.exportPreparing',
  'reservation.exportGenerating',
  'reservation.exportDownloading',
];

const RANGE_STATUS_KEYS = ['ticket.rangeProcessing'];

/** Normalise `GET /api/rifas/:skip/:limit` response shape */
function normalizeRifasResponse(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.rifas)) return raw.rifas;
  if (Array.isArray(raw.data)) return raw.data;
  if (raw._id) return [raw];
  return [];
}

const TABS = [
  { key: 'disponible', labelKey: 'ticket.availableTickets' },
  { key: 'vendido', labelKey: 'ticket.soldTickets' },
  { key: 'congelado', labelKey: 'ticket.frozenTickets' },
];

export default function TicketsList() {
  const { t } = useTranslation();
  const { state } = useContext(Store);
  const { userInfo } = state;

  const [selectedRifaId, setSelectedRifaId] = useState('');
  const [rifas, setRifas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const itemsPerPage = 10;
  const [availablePage, setAvailablePage] = useState(1);
  const [soldPage, setSoldPage] = useState(1);
  const [frozenPage, setFrozenPage] = useState(1);
  const [availableCount, setAvailableCount] = useState(0);
  const [soldCount, setSoldCount] = useState(0);
  const [frozenCount, setFrozenCount] = useState(0);

  const [availableTickets, setAvailableTickets] = useState([]);
  const [soldTickets, setSoldTickets] = useState([]);
  const [frozenTickets, setFrozenTickets] = useState([]);
  const [activeTab, setActiveTab] = useState('disponible');
  const [statusOverlayKeys, setStatusOverlayKeys] = useState(null);
  const [rangeModalMode, setRangeModalMode] = useState(null);
  const [rangeLoading, setRangeLoading] = useState(false);

  useEffect(() => {
    const fetchRifas = async () => {
      try {
        const raw = await rifaService.getRifas(0, 100);
        const list = normalizeRifasResponse(raw);
        setRifas(list);
        if (list.length > 0) setSelectedRifaId(list[0]._id);
      } catch (err) {
        setError(err.message);
        toast.error(t('common.errorOcurred'));
      }
    };
    fetchRifas();
  }, [t]);

  useEffect(() => {
    if (!selectedRifaId) return;
    setLoading(true);
    Promise.all([
      fetchTicketsByType('disponible', 0),
      fetchTicketsByType('vendido', 0),
      fetchTicketsByType('congelado', 0),
    ]).finally(() => setLoading(false));
  }, [selectedRifaId]);

  const fetchTicketsByType = async (type, skip) => {
    if (!selectedRifaId) return;
    try {
      const data = await ticketService.getTicketsByRifaAndType(
        selectedRifaId,
        type,
        skip,
        itemsPerPage
      );
      if (data) {
        switch (type) {
          case 'disponible':
            setAvailableTickets(data.tickets || []);
            setAvailableCount(data.totalAmount || 0);
            break;
          case 'vendido':
            setSoldTickets(data.tickets || []);
            setSoldCount(data.totalAmount || 0);
            break;
          case 'congelado':
            setFrozenTickets(data.tickets || []);
            setFrozenCount(data.totalAmount || 0);
            break;
          default:
            break;
        }
      }
    } catch (err) {
      setError(err.message);
      toast.error(t('common.errorOcurred'));
    }
  };

  const handleRifaChange = e => {
    const id = e.target.value;
    setSelectedRifaId(id);
    setActiveTab('disponible');
    setAvailablePage(1);
    setSoldPage(1);
    setFrozenPage(1);
  };

  const handleAvailablePageChange = page => {
    setAvailablePage(page);
    fetchTicketsByType('disponible', (page - 1) * itemsPerPage);
  };
  const handleSoldPageChange = page => {
    setSoldPage(page);
    fetchTicketsByType('vendido', (page - 1) * itemsPerPage);
  };
  const handleFrozenPageChange = page => {
    setFrozenPage(page);
    fetchTicketsByType('congelado', (page - 1) * itemsPerPage);
  };

  const handleFreezeTicket = async ticketId => {
    try {
      await ticketService.freezeTicket(ticketId);
      toast.success(t('ticket.freezeSuccess'));
      fetchTicketsByType('disponible', (availablePage - 1) * itemsPerPage);
      fetchTicketsByType('congelado', (frozenPage - 1) * itemsPerPage);
    } catch {
      toast.error(t('ticket.freezeError'));
    }
  };

  const handleUnfreezeTicket = async ticketId => {
    try {
      await ticketService.unfreezeTicket(ticketId);
      toast.success(t('ticket.unfreezeSuccess'));
      fetchTicketsByType('disponible', (availablePage - 1) * itemsPerPage);
      fetchTicketsByType('congelado', (frozenPage - 1) * itemsPerPage);
    } catch {
      toast.error(t('ticket.unfreezeError'));
    }
  };

  const refreshAvailableAndFrozen = () => {
    fetchTicketsByType('disponible', (availablePage - 1) * itemsPerPage);
    fetchTicketsByType('congelado', (frozenPage - 1) * itemsPerPage);
  };

  const handleRangeConfirm = async (from, to) => {
    if (!selectedRifaId || !rangeModalMode) return;
    setRangeLoading(true);
    setStatusOverlayKeys(RANGE_STATUS_KEYS);
    try {
      const result =
        rangeModalMode === 'freeze'
          ? await ticketService.freezeRange(selectedRifaId, from, to)
          : await ticketService.unfreezeRange(selectedRifaId, from, to);
      const successKey =
        rangeModalMode === 'freeze' ? 'ticket.freezeRangeSuccess' : 'ticket.unfreezeRangeSuccess';
      toast.success(
        t(successKey, {
          count: result.modifiedCount ?? 0,
          from: result.from ?? from,
          to: result.to ?? to,
        })
      );
      setRangeModalMode(null);
      refreshAvailableAndFrozen();
    } catch {
      toast.error(
        t(rangeModalMode === 'freeze' ? 'ticket.freezeRangeError' : 'ticket.unfreezeRangeError')
      );
    } finally {
      setRangeLoading(false);
      setStatusOverlayKeys(null);
    }
  };

  const handleExportExcel = async () => {
    setStatusOverlayKeys(EXPORT_STATUS_KEYS);
    try {
      const data = await ticketService.downloadExcel();
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'tickets.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t('ticket.exportError'));
    } finally {
      setStatusOverlayKeys(null);
    }
  };

  const tabCounts = {
    disponible: availableCount,
    vendido: soldCount,
    congelado: frozenCount,
  };

  return (
    <div className='space-y-6'>
      {statusOverlayKeys && <StatusLoadingOverlay messageKeys={statusOverlayKeys} />}
      {loading && <LoadingBox overlay={true} />}

      <TicketRangeModal
        show={Boolean(rangeModalMode)}
        mode={rangeModalMode || 'freeze'}
        onConfirm={handleRangeConfirm}
        onCancel={() => !rangeLoading && setRangeModalMode(null)}
        loading={rangeLoading}
      />

      <PageHeader
        title={t('ticket.ticketList') || 'Tickets'}
        subtitle='Consultá el estado de todos los tickets por bono'
        actions={
          <Button variant='outline' onClick={handleExportExcel}>
            <FileSpreadsheet className='h-4 w-4' />
            {t('ticket.exportExcel')}
          </Button>
        }
      />

      {/* Rifa selector + range actions */}
      <div className='flex flex-wrap items-center gap-3'>
        <label className='text-sm font-medium text-foreground' htmlFor='rifa-select'>
          {t('ticket.selectRaffle')}
        </label>
        <div className='relative'>
          <select
            id='rifa-select'
            value={selectedRifaId}
            onChange={handleRifaChange}
            className='h-9 appearance-none rounded-md border border-input bg-background pl-3 pr-8 text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
          >
            {rifas.map(rifa => (
              <option key={rifa._id} value={rifa._id}>
                {rifa.nombre}
              </option>
            ))}
          </select>
        </div>
        {selectedRifaId && (
          <>
            <Button
              variant='outline'
              size='sm'
              disabled={rangeLoading}
              onClick={() => setRangeModalMode('freeze')}
            >
              <Snowflake className='h-4 w-4' />
              {t('ticket.freezeRange')}
            </Button>
            <Button
              variant='outline'
              size='sm'
              disabled={rangeLoading}
              onClick={() => setRangeModalMode('unfreeze')}
            >
              <Sun className='h-4 w-4' />
              {t('ticket.unfreezeRange')}
            </Button>
          </>
        )}
      </div>

      {error && <p className='text-sm text-destructive'>{error}</p>}

      {/* Status tabs */}
      {selectedRifaId && (
        <>
          <div className='flex border-b border-border'>
            {TABS.map(tab => (
              <button
                key={tab.key}
                type='button'
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  '-mb-px px-4 py-2.5 text-sm font-medium transition-colors focus:outline-none',
                  activeTab === tab.key
                    ? 'border-b-2 border-primary text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {t(tab.labelKey)} ({tabCounts[tab.key]})
              </button>
            ))}
          </div>

          {activeTab === 'disponible' && (
            <TicketTable
              title={t('ticket.availableTickets')}
              tickets={availableTickets}
              loading={loading}
              totalCount={availableCount}
              currentPage={availablePage}
              onPageChange={handleAvailablePageChange}
              itemsPerPage={itemsPerPage}
              onFreeze={handleFreezeTicket}
              userInfo={userInfo}
              status='disponible'
            />
          )}
          {activeTab === 'vendido' && (
            <TicketTable
              title={t('ticket.soldTickets')}
              tickets={soldTickets}
              loading={loading}
              totalCount={soldCount}
              currentPage={soldPage}
              onPageChange={handleSoldPageChange}
              itemsPerPage={itemsPerPage}
              userInfo={userInfo}
              status='vendido'
            />
          )}
          {activeTab === 'congelado' && (
            <TicketTable
              title={t('ticket.frozenTickets')}
              tickets={frozenTickets}
              loading={loading}
              totalCount={frozenCount}
              currentPage={frozenPage}
              onPageChange={handleFrozenPageChange}
              itemsPerPage={itemsPerPage}
              onUnfreeze={handleUnfreezeTicket}
              userInfo={userInfo}
              status='congelado'
            />
          )}
        </>
      )}
    </div>
  );
}
